import { FilterModel, SearchViewModel } from '@models/module/grid';
export interface AttachmentModel {
  id: number;
  name: string;
  version: number;
  versionId: number;
  mimeType?: MimeTypesEnum;
  mimeTypeId?: number;
  parentId: number;
  length?: number;
  order?: number;
  thumbnail50?: string;
  thumbnail200?: string;
  note?: string;
  disabled?: boolean;
  highlighted?: boolean;
  bgColor?: string;
}

export enum MimeTypesEnum {
  PNG = 'image/png',
  PDF = 'application/pdf',
  TEXT_PLAIN = 'text/plain',
  JPEG = 'image/jpeg',
  DWG = 'image/x-dwg',
  HEIC = 'image/heic',
  HEIF = 'image/heif',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  PPTX2 = 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  DOC = 'application/msword',
  XLS = 'application/vnd.ms-excel',
  PPT = 'application/vnd.ms-powerpoint',
  WOPI_TEST = 'application/wopitest',
  WOPI_TEST2 = 'application/wopitestx',
  ZIP = 'application/x-zip-compressed',
  EMAIL = 'message/rfc822',
  XER = 'application/patch-ops-error+xml',
  XML = 'application/xml',
}

export const OfficeMimeTypes = [
  MimeTypesEnum.DOC,
  MimeTypesEnum.DOCX,
  MimeTypesEnum.XLS,
  MimeTypesEnum.XLSX,
  MimeTypesEnum.PPT,
  MimeTypesEnum.PPTX,
  MimeTypesEnum.PPTX2,
  MimeTypesEnum.WOPI_TEST,
  MimeTypesEnum.WOPI_TEST2,
];

export const PreviewableImageMimeTypes = [MimeTypesEnum.PNG, MimeTypesEnum.JPEG];

export const PreviewableMimeTypes = [...PreviewableImageMimeTypes, MimeTypesEnum.PDF, MimeTypesEnum.TEXT_PLAIN];

export interface FolderParentReference {
  id: number;
  isEntityInstance?: boolean;
}

export interface FileItemModel extends AttachmentModel {
  isShared: boolean;
  hasShared?: boolean;
  projectFilesName: string;
  myDriveName: string;
  setCompanyLogo: boolean;
  setUserLogo: boolean;
  createdBy: string;
  createdOn: string;
  updatedBy: string;
  updatedOn: string;
  isFolder: boolean;
  folderPath: string;
  parentName: string;
  isLocked: boolean;
  isReadOnly?: boolean;
  isDeleted: boolean;
  /** Where a deleted item used to live; only set for Recycle Bin listings. */
  pathWhenDeleted?: string;
  canDelete: boolean;
  folderPosition: number;
  parentsString: string;
  hasChildren: boolean;
  parents: (number | FolderParentReference)[];
  global?: number;
  permissions?: number[];
  /**
   * Set when the row is not a folder but an entity instance (contract/project listed inside Project
   * Files); its value is that entity's module id and `id` is the entity instance id.
   */
  itemModuleId?: number;
  /** Module of the Program/Project/Contract entity represented by this row. */
  rootModuleId?: number;
  /** Entity instance in which this particular folder/file occurrence is rendered. */
  parentEntityInstanceId?: number;
  /** Entity-instance lineage used to distinguish a global item rendered in multiple contexts. */
  parentsEIString?: string;
  contractEntityInstanceId?: number;
  projectEntityInstanceId?: number;
  programEntityInstanceId?: number;
  /** Entity instance the folder forwards its files to; set only on module destination folders. */
  destinationEntityInstanceId?: number;

  //   files
  thumbnail200: string;
  filePath: string;
  /** Set once the file has been loaded into PDF Markup, so it opens instead of uploading again. */
  markupDocumentId?: string;
}
export interface SearchFileModel {
  id: number;
  criteria?: string;
  search?: SearchViewModel;
  entityInstanceId?: number;
  /** Project Files only: module of the root context entity (Contract/Project/Program). Query param. */
  rootModuleId?: number;
  isFolders?: boolean;
  isGlobal?: boolean;
  mimeTypes?: MimeTypesEnum[];
  isDeletedItems?: boolean;
  page?: number;
  pagesize?: number;
  sort?: { field: string; order: number }[];
  /** Column filters (Name, Owner, dates, Shared) mapped to the backend filter model. */
  filter?: FilterModel;
  showAllPhotos?: boolean;
}

export interface FileChunkParams {
  start: number;
  end: number;
  chunks: number;
  file: File;
}

export const MimeTypesNotRecognizedByBrowserDict: Record<string, MimeTypesEnum> = {
  heic: MimeTypesEnum.HEIC,
  heif: MimeTypesEnum.HEIF,
  dwg: MimeTypesEnum.DWG,
  wopi: MimeTypesEnum.WOPI_TEST,
  wopix: MimeTypesEnum.WOPI_TEST2,
  wopitest: MimeTypesEnum.WOPI_TEST,
  wopitestx: MimeTypesEnum.WOPI_TEST2,
};

export const MimeTypesDict: Record<string, MimeTypesEnum> = {
  ...MimeTypesNotRecognizedByBrowserDict,
  doc: MimeTypesEnum.DOC,
  docx: MimeTypesEnum.DOCX,
  xls: MimeTypesEnum.XLS,
  xlsx: MimeTypesEnum.XLSX,
  pptx: MimeTypesEnum.PPTX,
  ppt: MimeTypesEnum.PPT,
  png: MimeTypesEnum.PNG,
  jpeg: MimeTypesEnum.JPEG,
  jpg: MimeTypesEnum.JPEG,
  '7z': MimeTypesEnum.ZIP,
  zip: MimeTypesEnum.ZIP,
  rar: MimeTypesEnum.ZIP,
  pdf: MimeTypesEnum.PDF,
  txt: MimeTypesEnum.TEXT_PLAIN,
  eml: MimeTypesEnum.EMAIL,
  xer: MimeTypesEnum.XER,
} as const;

export const ThumbnailsDict = {
  [MimeTypesEnum.PDF]: 'src/assets/icons/file-extensions/pdf.svg',
  [MimeTypesEnum.DOCX]: 'src/assets/icons/file-extensions/docx.svg',
  [MimeTypesEnum.XLSX]: 'src/assets/icons/file-extensions/xlsx.svg',
  [MimeTypesEnum.PPTX]: 'src/assets/icons/file-extensions/pptx.svg',
  [MimeTypesEnum.DOC]: 'src/assets/icons/file-extensions/docx.svg',
  [MimeTypesEnum.XLS]: 'src/assets/icons/file-extensions/xlsx.svg',
  [MimeTypesEnum.PPT]: 'src/assets/icons/file-extensions/pptx.svg',
  [MimeTypesEnum.ZIP]: 'src/assets/icons/file-extensions/zip.svg',
  [MimeTypesEnum.EMAIL]: 'src/assets/icons/file-extensions/email.svg',
  [MimeTypesEnum.PNG]: 'src/assets/icons/file-extensions/image.svg',
  [MimeTypesEnum.JPEG]: 'src/assets/icons/file-extensions/image.svg',
  [MimeTypesEnum.DWG]: 'src/assets/icons/file-extensions/image.svg',
  [MimeTypesEnum.HEIC]: 'src/assets/icons/file-extensions/image.svg',
  [MimeTypesEnum.HEIF]: 'src/assets/icons/file-extensions/image.svg',
  default: 'src/assets/icons/file-extensions/unknown.svg',
} as const;

export interface AttachemntFieldValueType extends AttachmentModel {
  createdOn?: string;
  updatedOn?: string;
  isFolder?: boolean;
  setCompanyLogo?: boolean;
  setUserLogo?: boolean;
  folderPosition?: number;
  isValid?: boolean;
  toDownload?: boolean;
  showMultipleSave?: boolean;
  projectFilesName?: string;
  myDriveName?: string;
  currentCulture?: number;
  currentCultureIgnoreCase?: number;
  invariantCulture?: number;
  invariantCultureIgnoreCase?: number;
  ordinal?: number;
  ordinalIgnoreCase?: number;
}
